import { load, dump } from 'js-yaml';

export interface DocSections {
  frontmatter: string;
  frontmatterObj: Record<string, any>;
  content: string;
  xml: string;
}

const YAML_DUMP_OPTIONS = {
  lineWidth: -1,
  noRefs: true,
  sortKeys: true
};

// Regex to match XML blocks at the bottom of the file
const XML_CONTAINER_REGEX = /<XML>([\s\S]*?)<\/XML>/gi;

// Regex to match frontmatter section
const FRONTMATTER_REGEX = /^---\n([\s\S]*?)\n---\n([\s\S]*)$/;

export function parseDocSections(fileString: string): DocSections {
  // First try to extract frontmatter
  const frontmatterMatch = fileString.match(FRONTMATTER_REGEX);
  let frontmatterRaw = '';
  let frontmatterObj: Record<string, any> = {};
  let contentSection = fileString;
  let xmlSection = '';

  if (!frontmatterMatch) {
    // No frontmatter, look for XML blocks in the entire file
    const xmlMatches = [...contentSection.matchAll(XML_CONTAINER_REGEX)];
    if (xmlMatches.length > 0) {
      // Combine all XML content
      xmlSection = xmlMatches.map(m => m[1].trim()).join('\n\n');
      // Remove all XML blocks from content
      contentSection = contentSection.replace(XML_CONTAINER_REGEX, '').trim();
    } else {
      contentSection = fileString.trim();
    }
  } else {
    frontmatterRaw = frontmatterMatch[1];
    const rest = frontmatterMatch[2];

    try {
      frontmatterObj = load(frontmatterRaw) || {};
    } catch {
      // If invalid YAML, frontmatterObj remains {}
    }

    // Look for XML blocks in the rest of the content
    const xmlMatches = [...rest.matchAll(XML_CONTAINER_REGEX)];
    if (xmlMatches.length > 0) {
      // Combine all XML content
      xmlSection = xmlMatches.map(m => m[1].trim()).join('\n\n');
      // Remove all XML blocks from content
      contentSection = rest.replace(XML_CONTAINER_REGEX, '').trim();
    } else {
      contentSection = rest.trim();
    }
  }

  return {
    frontmatter: frontmatterRaw.trim(),
    frontmatterObj,
    content: contentSection,
    xml: xmlSection
  };
}

export function rebuildDocSections(sections: DocSections): string {
  const frontmatterStr = dump(sections.frontmatterObj, YAML_DUMP_OPTIONS);
  let result = '';

  // Add frontmatter if we have any frontmatter object properties
  if (Object.keys(sections.frontmatterObj).length > 0) {
    result += `---\n${frontmatterStr}---\n\n`;
  }

  // Add content if present
  if (sections.content) {
    result += `${sections.content.trim()}\n`;
    // Only add extra newline if we have XML content
    if (sections.xml) {
      result += '\n';
    }
  }

  // Add XML section if present
  if (sections.xml) {
    result += `<XML>\n${sections.xml.trim()}\n</XML>\n`;
  }

  return result;
}

export function ensureTimestamps(meta: Record<string, any>): void {
  const now = new Date().toISOString();
  if (!meta.created) {
    meta.created = now;
  }
  meta.updated = now;
} 